import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { Theme } from '../../config/theme';
import { request } from '../../utils/Api';

const styles = StyleSheet.create({
  messageInputForm: {
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e0e0e0',
    width: '100%',
    [Theme.mobile_query]: {
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      backgroundColor: '#ffffff',
      boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)',
    },
  },
  messageInputInner: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '1rem',
    [Theme.mobile_query]: {
      padding: '0.75rem',
      paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0))',
    },
  },
  attachmentPreview: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '0.5rem',
  },
  attachmentItem: {
    position: 'relative',
    width: '60px',
    height: '60px',
    borderRadius: '0.25rem',
    overflow: 'hidden',
    border: '1px solid #dee2e6',
    marginTop: '4px',
    marginRight: '4px',
  },
  attachmentImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeAttachment: {
    position: 'absolute',
    top: '0px',
    right: '0px',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    backgroundColor: '#dc3545',
    color: '#ffffff',
    border: '2px solid #ffffff',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    zIndex: 1,
    ':hover': {
      backgroundColor: '#c82333',
    },
  },
  messageInputContainer: {
    display: 'flex',
    gap: '0.5rem',
    [Theme.mobile_query]: {
      gap: '0.75rem',
    },
  },
  messageInput: {
    flex: 1,
    padding: '0.5rem 1rem',
    border: '1px solid #ced4da',
    borderRadius: '1.5rem',
    fontSize: '16px',
    '-webkit-text-size-adjust': '100%',
    '-moz-text-size-adjust': '100%',
    '-ms-text-size-adjust': '100%',
    [Theme.mobile_query]: {
      padding: '0.75rem 1rem',
      fontSize: '16px', // Prevents zoom on iOS
      borderRadius: '2rem',
      minHeight: '44px', // iOS recommended touch target
    },
  },
  attachButton: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    border: 'none',
    borderRadius: '50%',
    width: '2.5rem',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#dee2e6',
    },
    [Theme.mobile_query]: {
      width: '44px',
      height: '44px',
    },
  },
  sendButton: {
    backgroundColor: '#0084ff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '50%',
    width: '2.5rem',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#0056b3',
    },
    ':disabled': {
      backgroundColor: '#ccc',
      cursor: 'not-allowed',
    },
    [Theme.mobile_query]: {
      width: '44px',
      height: '44px',
      fontSize: '1.25rem',
    },
  },
  hiddenFileInput: {
    display: 'none',
  },
});

export function MessageInput({ onSendMessage, onInputFocus, accessToken }) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      if (message.trim() || attachments.length > 0) {
        onSendMessage(
          message.trim(),
          attachments.map(a => a.id),
        );
        setMessage('');
        setAttachments([]);
      }
    },
    [message, attachments, onSendMessage],
  );

  const handleChange = useCallback(e => {
    setMessage(e.target.value);
  }, []);

  const handleFocus = useCallback(() => {
    // Call the onInputFocus prop if provided
    if (onInputFocus) {
      onInputFocus();
    }
  }, [onInputFocus]);

  const handleAttachClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileSelect = useCallback(
    async e => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      // Reset file input
      e.target.value = '';

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      setIsUploading(true);

      try {
        // Convert to base64
        const reader = new FileReader();
        reader.onload = async event => {
          const base64 = event.target.result;

          // Upload to server
          try {
            const response = await request(
              'messages/attachment/upload',
              'POST',
              { image: base64 },
              {
                Authorization: `Bearer ${accessToken}`,
              },
            );

            if (response.status !== 200) {
              throw new Error(`Upload failed: ${response.status}`);
            }

            // Add to attachments
            setAttachments(prev => [
              ...prev,
              {
                id: response.data.data.id,
                url: response.data.data.url,
                type: response.data.data.type,
                filename: response.data.data.filename || file.name,
              },
            ]);
          } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload image. Please try again.');
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('File read error:', error);
        alert('Failed to read image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    },
    [accessToken],
  );

  const handleRemoveAttachment = useCallback(id => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  // Determine send button tooltip
  const getSendButtonTooltip = () => {
    if (isUploading) return 'Uploading image...';
    if (!message.trim() && attachments.length === 0) return 'Type a message or attach an image';
    return 'Send message';
  };

  return (
    <form onSubmit={handleSubmit} className={css(styles.messageInputForm)}>
      <div className={css(styles.messageInputInner)}>
        {attachments.length > 0 && (
          <div className={css(styles.attachmentPreview)}>
            {attachments.map(attachment => (
              <div key={attachment.id} className={css(styles.attachmentItem)}>
                <img
                  src={attachment.url}
                  alt={attachment.filename}
                  className={css(styles.attachmentImage)}
                />
                <button
                  type="button"
                  className={css(styles.removeAttachment)}
                  onClick={() => handleRemoveAttachment(attachment.id)}
                  aria-label="Remove attachment"
                  title="Remove attachment"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={css(styles.messageInputContainer)}>
          <input
            type="text"
            value={message}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="Type a message..."
            className={css(styles.messageInput)}
            aria-label="Message input"
            autoFocus={!Theme.isMobile} // Only autofocus on desktop
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className={css(styles.hiddenFileInput)}
            aria-label="Select image"
          />
          <button
            type="button"
            className={css(styles.attachButton)}
            onClick={handleAttachClick}
            disabled={isUploading}
            aria-label="Attach image"
            title="Attach an image"
          >
            <span role="img" aria-label="Paperclip">
              📎
            </span>
          </button>
          <button
            type="submit"
            className={css(styles.sendButton)}
            disabled={(!message.trim() && attachments.length === 0) || isUploading}
            aria-label={getSendButtonTooltip()}
            title={getSendButtonTooltip()}
          >
            →
          </button>
        </div>
      </div>
    </form>
  );
}

MessageInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  onInputFocus: PropTypes.func,
  accessToken: PropTypes.string,
};

MessageInput.defaultProps = {
  onInputFocus: null,
  accessToken: null,
};



// WEBPACK FOOTER //
// ./src/views/MessengerConversation/message-input.js